import { Constants, getRunningTimeMinutes } from "@eagerdog/constants";

import { IRunningOrder } from "./running-order.interface";
import { IRunningOrderDogClass } from "./running-order-dog-class.interface";
import { IShowRing } from "./show-ring.interface";

export interface IRunningOrderClassRef {
  show_element?: string;
  level?: string;
  section?: string;
  type?: string;
  split_number?: number;
  entries?: number;
  dog_run_order?: string[];
}

export interface IRunningOrderJudgeRef {
  judge_name?: string;
  judge_number?: string;
  dog_classes?: IRunningOrderClassRef[];
}

export interface IScoreJudgeRef {
  judge_name?: string;
  dog_class?: {
    show_element?: string;
    level?: string;
    section?: string;
  };
}

const BREAK_TYPE = "break";

export interface IShowElementClassRef {
  show_element?: string;
  level?: string;
  section?: string;
  judge_name?: string;
  judge_number?: string;
  num_hides?: number;
  time_limit?: string;
}

/**
 * A class is offered when the show's element setup includes it.
 * A row with no level and no section offers the whole element.
 * A row with a level (AKC Scent Work) offers only that class.
 * An empty element list does not hide anything.
 */
export function isOfferedShowClass(
  showElements: IShowElementClassRef[] | undefined,
  dc: IRunningOrderClassRef
): boolean {
  if (isBreakRunningOrderClass(dc)) {
    return true;
  }
  const elements = showElements || [];
  if (elements.length === 0) {
    return true;
  }
  const sameElement = elements.filter((se) => se.show_element === dc.show_element);
  if (sameElement.length === 0) {
    return false;
  }
  if (sameElement.some((se) => !se.level && !se.section)) {
    return true;
  }
  return sameElement.some(
    (se) =>
      (se.level || "") === (dc.level || "") &&
      (se.section || "") === (dc.section || "")
  );
}

/**
 * Judges the Edit Show list should show. Class setup and the ring schedule
 * both count. A judge added on the schedule has no class row yet.
 */
export function judgesListedOnShow(show: {
  show_elements?: Array<{ judge_name?: string; judge_number?: string }>;
  rings?: Array<{ running_order?: Array<{ judge_name?: string; judge_number?: string }> }>;
}): Array<{ judge_name: string; judge_number: string }> {
  const listed = new Map<string, { judge_name: string; judge_number: string }>();
  const add = (name?: string, number?: string) => {
    const judgeName = (name || "").trim();
    if (!judgeName) {
      return;
    }
    const judgeNumber = (number || "").trim();
    const key = `${judgeName.toLowerCase()}|${judgeNumber}`;
    if (!listed.has(key)) {
      listed.set(key, { judge_name: judgeName, judge_number: judgeNumber });
    }
  };
  (show.show_elements || []).forEach((element) => add(element.judge_name, element.judge_number));
  (show.rings || []).forEach((ring) => {
    (ring.running_order || []).forEach((order) => add(order.judge_name, order.judge_number));
  });
  return Array.from(listed.values());
}

function offeredClassKey(dc: IShowElementClassRef): string {
  return `${dc.show_element || ""}|${dc.level || ""}|${dc.section || ""}`;
}

function sameAssignedJudge(
  aName?: string,
  aNumber?: string,
  bName?: string,
  bNumber?: string
): boolean {
  const nameA = (aName || "").trim();
  const nameB = (bName || "").trim();
  if (!nameA || nameA !== nameB) {
    return false;
  }
  const numberA = (aNumber || "").trim();
  const numberB = (bNumber || "").trim();
  if (!numberA || !numberB) {
    return true;
  }
  return numberA === numberB;
}

function classHasDogs(dc: IRunningOrderClassRef): boolean {
  return (dc.entries || 0) > 0 || (dc.dog_run_order || []).length > 0;
}

/**
 * Keep a built ring in step with the classes the show offers.
 * An unchecked class leaves the ring when it has no dogs.
 * A class checked after the ring was built is added under its judge.
 */
export function reconcileOfferedClassesInRings(
  rings: IShowRing[] | undefined,
  showElements: IShowElementClassRef[] | undefined,
  options?: { sanctioningClub?: string; showType?: string }
): { rings: IShowRing[]; changed: boolean } {
  const source = rings || [];
  const hasRunningOrder = source.some((ring) => (ring.running_order || []).length > 0);
  if (!hasRunningOrder) {
    return { rings: source, changed: false };
  }

  const next: IShowRing[] = source.map((ring) => ({
    ...ring,
    running_order: (ring.running_order || []).map((order) => ({
      ...order,
      dog_classes: [...(order.dog_classes || [])],
    })),
  }));

  let changed = false;
  const elements = showElements || [];

  next.forEach((ring) => {
    (ring.running_order || []).forEach((order) => {
      const kept = order.dog_classes.filter((dc) => {
        if (isBreakRunningOrderClass(dc)) {
          return true;
        }
        if (isOfferedShowClass(elements, dc)) {
          return true;
        }
        if (classHasDogs(dc)) {
          return true;
        }
        changed = true;
        return false;
      });
      if (kept.length !== order.dog_classes.length) {
        order.dog_classes = kept;
      }
    });
  });

  const present = new Set<string>();
  next.forEach((ring) => {
    (ring.running_order || []).forEach((order) => {
      order.dog_classes.forEach((dc) => {
        if (!isBreakRunningOrderClass(dc) && dc.show_element && dc.level) {
          present.add(offeredClassKey(dc));
        }
      });
    });
  });

  elements.forEach((element) => {
    if (!element.show_element || !element.level) {
      return;
    }
    const key = offeredClassKey(element);
    if (present.has(key)) {
      return;
    }

    const target = placeOfferedClass(next, element);
    if (!target) {
      return;
    }

    const duration =
      options?.sanctioningClub && options.showType
        ? getRunningTimeMinutes(
            options.sanctioningClub,
            options.showType,
            element.show_element,
            element.level
          )
        : undefined;

    const dogClass: IRunningOrderDogClass = {
      type: Constants.running_order_dog_class_type.dog_class,
      show_element: element.show_element,
      level: element.level,
      ...(element.section ? { section: element.section } : {}),
      ...(element.num_hides !== undefined ? { num_hides: element.num_hides } : {}),
      ...(element.time_limit ? { time_limit: element.time_limit } : {}),
      entries: 0,
      duration,
      total_minutes: 0,
      dog_run_order: [],
    };
    target.dog_classes.push(dogClass);
    present.add(key);
    changed = true;
  });

  return { rings: changed ? next : source, changed };
}

function placeOfferedClass(
  rings: IShowRing[],
  element: IShowElementClassRef
): IRunningOrder | undefined {
  const matches: IRunningOrder[] = [];
  rings.forEach((ring) => {
    (ring.running_order || []).forEach((order) => {
      if (
        sameAssignedJudge(
          order.judge_name,
          order.judge_number,
          element.judge_name,
          element.judge_number
        )
      ) {
        matches.push(order);
      }
    });
  });

  const withElement = matches.find((order) =>
    order.dog_classes.some(
      (dc) =>
        !isBreakRunningOrderClass(dc) && dc.show_element === element.show_element
    )
  );
  if (withElement) {
    return withElement;
  }
  if (matches.length > 0) {
    return matches[0];
  }

  const namedJudges = rings.flatMap((ring) =>
    (ring.running_order || []).filter((order) => (order.judge_name || "").trim())
  );
  if ((element.judge_name || "").trim()) {
    const ring =
      rings.find((item) => item.ring === Constants.ring_names.ring_1) || rings[0];
    if (!ring) {
      return undefined;
    }
    if (!ring.running_order) {
      ring.running_order = [];
    }
    const created: IRunningOrder = {
      judge_name: (element.judge_name || "").trim(),
      judge_number: element.judge_number || "",
      dog_classes: [],
      total_minutes: 0,
    };
    ring.running_order.push(created);
    return created;
  }

  return namedJudges[0];
}

export function runningOrderClassKey(
  dc: IRunningOrderClassRef,
  options?: { ignoreSection?: boolean }
): string {
  const section = options?.ignoreSection ? "" : dc.section || "";
  return `${dc.show_element || ""}|${dc.level || ""}|${section}`;
}

export function sameRunningOrderClass(
  a: IRunningOrderClassRef,
  b: IRunningOrderClassRef,
  options?: { ignoreSection?: boolean }
): boolean {
  return runningOrderClassKey(a, options) === runningOrderClassKey(b, options);
}

export function isSplitRunningOrderClass(dc: IRunningOrderClassRef): boolean {
  return dc.split_number !== undefined;
}

export function isBreakRunningOrderClass(dc: IRunningOrderClassRef): boolean {
  return dc.type === BREAK_TYPE;
}

export function runningOrderTileActivity(dc: IRunningOrderClassRef): number {
  if (isBreakRunningOrderClass(dc)) {
    return -1;
  }
  return (dc.entries || 0) * 10 + ((dc.dog_run_order || []).length > 0 ? 1 : 0);
}

/**
 * A leftover tile is the same unsplit class under a second judge after the
 * class was moved. Judge Schedule hides these (0 live entries). Run Order,
 * Word export, and catalogs must ignore them too or they print the wrong judge.
 *
 * Split tiles (split_number) are intentional and are never leftovers.
 */
export function isLeftoverRunningOrderClass(
  tileJudgeName: string | undefined,
  dc: IRunningOrderClassRef,
  runningOrders: IRunningOrderJudgeRef[] | undefined,
  scores?: IScoreJudgeRef[],
  options?: { ignoreSection?: boolean }
): boolean {
  if (!dc || isBreakRunningOrderClass(dc) || isSplitRunningOrderClass(dc)) {
    return false;
  }
  if (!dc.show_element || !dc.level) {
    return false;
  }

  const siblings = (runningOrders || []).flatMap((ro) =>
    (ro.dog_classes || [])
      .filter(
        (other) =>
          other !== dc &&
          !isBreakRunningOrderClass(other) &&
          !isSplitRunningOrderClass(other) &&
          sameRunningOrderClass(dc, other, options)
      )
      .map((other) => ({ ro, dc: other }))
  );

  if (siblings.length === 0) {
    return false;
  }

  const tileJudge = (tileJudgeName || "").trim();
  const classScores = (scores || []).filter(
    (score) =>
      score.dog_class?.show_element === dc.show_element &&
      score.dog_class?.level === dc.level &&
      (options?.ignoreSection ||
        (score.dog_class?.section || "") === (dc.section || ""))
  );
  const assignedJudges = new Set(
    classScores
      .map((score) => (score.judge_name || "").trim())
      .filter((name) => name.length > 0)
  );

  if (assignedJudges.size > 0) {
    return !assignedJudges.has(tileJudge);
  }

  const thisActivity = runningOrderTileActivity(dc);
  const siblingActivity = Math.max(
    ...siblings.map((sibling) => runningOrderTileActivity(sibling.dc))
  );
  return thisActivity === 0 && siblingActivity > 0;
}

/**
 * Prefer the running-order row whose matching class tile actually has entries
 * or a dog_run_order. First-match-wins picks leftover empty tiles and is what
 * printed the wrong AKC Scent Work catalog judge.
 */
export function findActiveRunningOrder(
  rings: IShowRing[] | undefined,
  showElement: string,
  level?: string,
  options?: { ring?: string; ignoreSection?: boolean; section?: string }
): IRunningOrder | null {
  if (!rings?.length || !showElement) {
    return null;
  }

  const ringsToSearch = options?.ring
    ? rings.filter((ring) => ring.ring === options.ring)
    : rings;

  let best: { runningOrder: IRunningOrder; activity: number } | null = null;

  for (const ring of ringsToSearch) {
    for (const runningOrder of ring.running_order || []) {
      const matchingClasses = (runningOrder.dog_classes || []).filter((dc) => {
        if (isBreakRunningOrderClass(dc)) {
          return false;
        }
        if (dc.show_element !== showElement) {
          return false;
        }
        if (level && dc.level !== level) {
          return false;
        }
        if (
          options?.section !== undefined &&
          !options.ignoreSection &&
          (dc.section || "") !== (options.section || "")
        ) {
          return false;
        }
        return true;
      });

      if (matchingClasses.length === 0) {
        continue;
      }

      const activity = Math.max(
        ...matchingClasses.map((dc) => runningOrderTileActivity(dc))
      );
      if (!best || activity > best.activity) {
        best = { runningOrder, activity };
      }
    }
  }

  return best?.runningOrder || null;
}
